import { FC, useMemo } from "react";
import { Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next"; // Import useTranslation

import { ActivePageType } from "features/Experiment/hooks/useExperimentContainer";

import Flag from "icons/Flag";
import Exit from "icons/Exit";

import { Button } from "components/common/Button";
import { ConfirmationModal } from "components/common/modals/ConfirmationModal";
import { Alert } from "components/common/Alert";
import { ToastMessage } from "components/common/ToastMessage";

import {
  StyledExperimentProducts,
  StyledExperimentProductsHeader,
  StyledExperimentProductsSubheader,
  StyledExperimentProductsContainer,
  StyledExperimentProductsList,
  StyledExperimentProductContainer,
  StyledExperimentProduct,
} from "styles/features/experiment";

interface ExperimentProductsProps {
  experimentName?: string;
  products: string[];
  isExperimentLeaveModalOpen?: boolean;
  isNeedHelp?: boolean;
  error?: string | null;

  handleToastClose: () => void;
  onHelpAsk?: () => void;
  onExperimentLeaveModalOpen?: () => void;
  onExperimentLeaveModalClose?: () => void;
  onExperimentLeave?: () => void;
  onNextStep?: (newPage: ActivePageType) => void;
  onNeedHelpCancel?: () => void;
}

const ExperimentProducts: FC<ExperimentProductsProps> = ({
  experimentName,
  products,
  isExperimentLeaveModalOpen,
  isNeedHelp,
  error,
  onHelpAsk,
  onExperimentLeave,
  onExperimentLeaveModalOpen,
  onExperimentLeaveModalClose,
  onNextStep,
  onNeedHelpCancel,
  handleToastClose,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(); // Initialize translation

  const renderedNeedHelpInfo = useMemo(
    () =>
      isNeedHelp ? (
        <Alert
          severity="info"
          text={t("help_on_way")}
          className="need-help-info"
          tools={
            <Button size="xs" onClick={onNeedHelpCancel} variant="text">
              {t("cancel")}
            </Button>
          }
        />
      ) : null,
    [isNeedHelp, onNeedHelpCancel, t]
  );

  return (
    <StyledExperimentProducts>
      <StyledExperimentProductsHeader>
        <Typography
          variant="h1"
          color={theme.palette.common.black}
          marginBottom="8px"
        >
          {experimentName}
        </Typography>
        <Button
          size="md"
          startIcon={<Exit />}
          variant="secondary"
          maxWidth="350px"
          onClick={onExperimentLeaveModalOpen}
        >
          {t("exit_experiment")}
        </Button>
      </StyledExperimentProductsHeader>
      <StyledExperimentProductsSubheader>
        {renderedNeedHelpInfo}
        <Button
          variant="grey"
          size="md"
          startIcon={<Flag />}
          className="experiment-need-help"
          maxWidth="290px"
          onClick={onHelpAsk}
          disabled={isNeedHelp}
        >
          {t("need_help")}
        </Button>
        <Stack
          className="experiment-products-headings"
          display="flex"
          flexDirection="column"
          gap="8px"
        >
          <Typography
            variant="h2"
            color={theme.palette.common.black}
            textAlign={theme.direction === "rtl" ? "end" : "start"}
          >
            {t("take_prototypes")}
          </Typography>
          <Typography
            variant="bodyL"
            color={theme.palette.common.black}
            textAlign={theme.direction === "rtl" ? "end" : "start"}
          >
            {t("tasting_includes")}
          </Typography>
        </Stack>
      </StyledExperimentProductsSubheader>
      <StyledExperimentProductsContainer>
        <StyledExperimentProductsList>
          <StyledExperimentProductContainer>
            <StyledExperimentProduct>
              <Typography variant="h2" color={theme.palette.common.black}>
                {products[0]}
              </Typography>
            </StyledExperimentProduct>
            <Typography
              variant="h3"
              color={theme.palette.customGrey.dark}
              fontWeight={300}
            >
              {t("option_1")}
            </Typography>
          </StyledExperimentProductContainer>
          <StyledExperimentProductContainer>
            <StyledExperimentProduct>
              <Typography variant="h2" color={theme.palette.common.black}>
                {products[1]}
              </Typography>
            </StyledExperimentProduct>
            <Typography
              variant="h3"
              color={theme.palette.customGrey.dark}
              fontWeight={300}
            >
              {t("option_2")}
            </Typography>
          </StyledExperimentProductContainer>
        </StyledExperimentProductsList>
        <Button size="md" onClick={() => onNextStep?.("tasting")}>
          {t("next_step")}
        </Button>
      </StyledExperimentProductsContainer>
      <ConfirmationModal
        open={!!isExperimentLeaveModalOpen}
        type="info"
        title={t("exit_experiment_title")}
        text={t("exit_experiment_text")}
        submitBtnText={t("confirm_exit")}
        cancelBtnText={t("stay_in_experiment")}
        onClose={onExperimentLeaveModalClose}
        onCancel={onExperimentLeaveModalClose}
        onSubmit={onExperimentLeave}
      />
      <ToastMessage
        severity="error"
        open={!!error}
        message={error as string}
        onClose={handleToastClose}
      />
    </StyledExperimentProducts>
  );
};

export default ExperimentProducts;
